import * as MP4Box from "mp4box";
import * as Mediabunny from "mediabunny";

let mp4boxFile = null;
let audioTrack = null;
let videoTrack = null;
let videoDescription = null;
let audioDescription = null;
let videoDone = false;
let audioDone = false;
let mediaDone = false;
let audioSamples = [];

/**
 * 获取视频旋转角度
 * @param {*} track
 * @returns
 */
const getRotatedFromTrack = (track) => {
  // const w = track.track_width || track.width || 0;
  // const h = track.track_height || track.height || 0;
  const m = track.matrix; // 你的 [0, 65536, 0, -65536, 0, 0, 0, 0, 1073741824]

  // 转为浮点（16.16 定点）
  const a = m[0] / 65536;
  const b = m[1] / 65536;
  const c = m[3] / 65536;
  const d = m[4] / 65536;

  const TOL = 0.01; // tolerance for floating point comparison

  if (
    Math.abs(a - 1) < TOL &&
    Math.abs(b - 0) < TOL &&
    Math.abs(c - 0) < TOL &&
    Math.abs(d - 1) < TOL
  ) {
    return 0;
  }

  if (
    Math.abs(a - 0) < TOL &&
    Math.abs(b - 1) < TOL &&
    Math.abs(c + 1) < TOL &&
    Math.abs(d - 0) < TOL
  ) {
    return 90;
  }

  if (
    Math.abs(a + 1) < TOL &&
    Math.abs(b - 0) < TOL &&
    Math.abs(c - 0) < TOL &&
    Math.abs(d + 1) < TOL
  ) {
    return 180;
  }

  if (
    Math.abs(a - 0) < TOL &&
    Math.abs(b + 1) < TOL &&
    Math.abs(c - 1) < TOL &&
    Math.abs(d - 0) < TOL
  ) {
    return 270;
  }

  return 0;
};

const DataStream = MP4Box.DataStream;
// 获取解码器需要的 description（SPS/PPS）
const getDescription = (mp4boxFile, audioCodec = "") => {
  let i = 0;
  let audioDescription = null;
  let videoDescription = null;
  const len = Math.min(mp4boxFile.moov.traks.length, 3);
  while (i < len) {
    const entry = mp4boxFile.moov.traks[i].mdia.minf.stbl.stsd.entries[0];
    const box = entry.avcC ?? entry.hvcC ?? entry.vpcC;
    if (box != null) {
      const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
      box.write(stream);
      // slice()方法的作用是移除moov box的header信息
      videoDescription = new Uint8Array(stream.buffer.slice(8));
    } else {
      switch (audioCodec.split(".")[0]) {
        case "mp4a":
          if (entry?.esds?.esd?.descs[0]) {
            audioDescription = entry.esds.esd.descs[0].descs[0].data;
          }
          // mov 视频音频对象会嵌套在 wave 盒子中
          else if (entry?.wave?.esds?.esd?.descs[0]) {
            audioDescription = entry.wave.esds.esd.descs[0].descs[0].data;
          }
          break;
        default:
          // 移动端视频基本都是 mp4a，其他音频格式有空再研究了
          break;
      }
    }
    if (videoDescription && audioDescription) {
      return { videoDescription, audioDescription };
    }
    i++;
  }
  return {
    videoDescription,
    audioDescription,
  };
};

// 使用 Mediabunny 封装视频
const muxVideo = async (encodedChunks) => {
  console.log("encodedChunks:", encodedChunks.length);
  console.log(
    "audioSamples:",
    audioSamples ? audioSamples.length : 0,
    "audioTrack:",
    audioTrack?.id,
  );
  try {
    console.log(`开始封装 ${encodedChunks.length} 个编码块`);

    // 检查第一个编码块的 metadata
    if (encodedChunks.length > 0) {
      console.log("第一个编码块:", JSON.stringify(encodedChunks[0]));
      console.log(
        "第一个编码块的 metadata:",
        JSON.stringify(encodedChunks[0].metadata),
      );
    }

    // 使用 Mediabunny 创建 Output
    const output = new Mediabunny.Output({
      target: new Mediabunny.BufferTarget(), // 使用 BufferTarget 而不是 ArrayBufferTarget
      format: new Mediabunny.Mp4OutputFormat({
        fastStart: "in-memory", // Mediabunny 支持的 fastStart 选项
      }),
    });

    // 创建一个 VideoSource 用于 Mediabunny
    // 回到 EncodedVideoPacketSource，但尝试不同的方法
    const videoSource = new Mediabunny.EncodedVideoPacketSource("avc");

    console.log("Mediabunny EncodedVideoPacketSource 创建成功，codec: avc");

    const rotation = getRotatedFromTrack(videoTrack);
    // 添加视频轨道
    output.addVideoTrack(videoSource, {
      rotation,
    });

    // 如果有音频轨道，创建音频源并添加
    let audioCodec = "aac"; // 默认使用 AAC
    let audioSource = null;
    if (audioTrack && audioSamples && audioSamples.length > 0) {
      // 从 audioTrack 中获取音频编解码器信息
      // 常见的音频编解码器：'mp4a' (AAC), 'opus', 'vorbis' 等
      if (audioTrack.codec) {
        // 从 codec 字符串中提取编解码器类型
        // 例如: 'mp4a.40.2' -> 'mp4a'
        const codecMatch = audioTrack.codec.match(/^([a-z0-9]+)/);
        if (codecMatch) {
          audioCodec = codecMatch[1];
        }
      }
      if (audioCodec === "mp4a") {
        audioCodec = "aac"; // Mediabunny 使用 'aac' 表示 AAC 编解码器
      }

      console.log(
        "创建音频源，codec:",
        audioCodec,
        "音频样本数:",
        audioSamples.length,
      );
      audioSource = new Mediabunny.EncodedAudioPacketSource(audioCodec);

      // 添加音频轨道
      output.addAudioTrack(audioSource, {
        // language 有可能是非法字符，不设置了
        // languageCode: audioTrack.language,
        name: audioTrack.name,
      });
      console.log("音频轨道已添加到输出");
    }

    // 启动 Output - 这是关键步骤！
    await output.start();

    // 按时间戳排序编码块
    const sortedChunks = encodedChunks.sort(
      (a, b) => a.chunk.timestamp - b.chunk.timestamp,
    );

    // 添加视频样本到 Mediabunny
    let totalDataSize = 0;
    const originalFramerate =
      videoTrack.nb_samples / (videoTrack.duration / videoTrack.timescale);
    console.log(
      "原视频每秒帧数：",
      originalFramerate,
      " 码率：",
      videoTrack.bitrate,
    );
    for (let i = 0; i < sortedChunks.length; i++) {
      const item = sortedChunks[i];
      const chunk = item.chunk;

      try {
        totalDataSize += chunk.byteLength;

        // 每100个 chunk 打印一次进度
        if (i % 100 === 0 && i > 0) {
          console.log(
            `已添加 ${i}/${sortedChunks.length} 个 chunk 到 VideoSource`,
          );
        }

        const buffer = new Uint8Array(chunk.data);

        const encodedPacket = new Mediabunny.EncodedPacket(
          buffer, // Uint8Array 数据
          chunk.type, // 'key' 或 'delta' 字符串
          chunk.timestamp / 1000000, // 转换为秒
          chunk.duration
            ? chunk.duration / 1000000
            : 1 / (originalFramerate || 24), // 转换为秒
        );

        videoSource.add(encodedPacket, item.metadata);

        if (i === 0) {
          console.log("第一个 chunk 添加成功 (Mediabunny)");
          console.log("chunk 类型:", chunk.constructor.name);
          console.log("EncodedPacket 创建成功");
          console.log(
            "chunk 属性:",
            JSON.stringify({
              type: chunk.type,
              timestamp: chunk.timestamp,
              duration: chunk.duration,
              byteLength: chunk.byteLength,
            }),
          );
        }

        // 每处理100个chunk打印一次进度
        if (i % 100 === 0 && i > 0) {
          console.log(`已处理 ${i}/${sortedChunks.length} 个 chunks`);
        }
      } catch (chunkError) {
        console.error(`Mediabunny 添加第 ${i} 个 chunk 失败:`, chunkError);
        console.error(
          "chunk 详细信息:",
          JSON.stringify({
            type: chunk.type,
            timestamp: chunk.timestamp,
            duration: chunk.duration,
            byteLength: chunk.byteLength,
            constructor: chunk.constructor.name,
          }),
        );

        // Mediabunny 如果失败，直接抛出错误，不再尝试其他方法
        throw new Error(
          `Mediabunny 处理第 ${i} 个 chunk 失败: ${chunkError.message}`,
        );
      }

      // 更新进度
    }

    console.log(
      `所有视频数据添加完成，总数据大小: ${totalDataSize} bytes，共 ${sortedChunks.length} 个 chunks`,
    );

    // 添加音频样本到音频源
    if (
      audioSource &&
      audioSamples &&
      audioSamples.length > 0 &&
      audioDescription
    ) {
      console.log(`开始添加 ${audioSamples.length} 个音频样本`);

      // 按时间戳排序音频样本
      const sortedAudioSamples = audioSamples.sort((a, b) => {
        const timeA = (a.cts * 1000000) / audioTrack.timescale;
        const timeB = (b.cts * 1000000) / audioTrack.timescale;
        return timeA - timeB;
      });

      const audioPacketMetadata = {
        decoderConfig: {
          codec: "mp4a.40.2",
          description: audioDescription,
          numberOfChannels: audioTrack.audio.channel_count,
          sampleRate: audioTrack.audio.sample_rate,
        },
      };
      console.log("audioPacket metadata:", audioPacketMetadata);

      for (let i = 0; i < sortedAudioSamples.length; i++) {
        const sample = sortedAudioSamples[i];
        try {
          // 将音频样本转换为 Mediabunny 需要的格式
          const timestamp = (sample.cts * 1000000) / audioTrack.timescale; // 转换为微秒
          const duration = (sample.duration * 1000000) / audioTrack.timescale; // 转换为微秒

          // 创建 EncodedPacket
          const audioPacket = new Mediabunny.EncodedPacket(
            sample.data, // Uint8Array 数据
            sample.is_sync ? "key" : "delta", // 类型
            timestamp / 1000000, // 转换为毫秒
            duration / 1000000, // 转换为毫秒
          );

          audioSource.add(audioPacket, audioPacketMetadata);

          // 每100个样本打印一次进度
          if (i % 100 === 0 && i > 0) {
            console.log(`已添加 ${i}/${sortedAudioSamples.length} 个音频样本`);
          }
        } catch (audioError) {
          console.error(`添加第 ${i} 个音频样本失败:`, audioError);
          // 继续处理其他样本，不中断整个流程
        }
      }

      console.log(
        `所有音频样本添加完成，共 ${sortedAudioSamples.length} 个样本`,
      );
    }

    // 尝试手动关闭 VideoSource
    try {
      if (typeof videoSource._flushAndClose === "function") {
        console.log("调用 VideoSource._flushAndClose()");
        await videoSource._flushAndClose();
      } else {
        console.log("VideoSource 没有 _flushAndClose 方法");
      }
    } catch (closeError) {
      console.log("关闭 VideoSource 时出错:", closeError.message);
    }

    // 尝试手动关闭 AudioSource
    if (audioSource) {
      try {
        if (typeof audioSource._flushAndClose === "function") {
          console.log("调用 AudioSource._flushAndClose()");
          await audioSource._flushAndClose();
        } else {
          console.log("AudioSource 没有 _flushAndClose 方法");
        }
      } catch (audioCloseError) {
        console.log("关闭 AudioSource 时出错:", audioCloseError.message);
      }
    }

    console.log("开始 finalize (Mediabunny)...");

    // 等待更长时间确保所有异步操作完成
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      // 尝试取消并重新 finalize（某些情况下可能需要）
      console.log("开始 Mediabunny finalize...");
      await output.finalize();
      console.log("Output finalize 完成");

      // 检查 target 状态
      console.log("Target buffer 状态:", {
        hasBuffer: !!output.target.buffer,
        bufferSize: output.target.buffer ? output.target.buffer.byteLength : 0,
      });
    } catch (finalizeError) {
      console.error("Finalize 过程出错:", finalizeError);
      throw finalizeError;
    }

    // 从 target 获取封装后的数据
    const buffer = output.target.buffer;

    console.log(`Mediabunny 封装完成，文件大小: ${buffer.byteLength} bytes`);

    return buffer;
  } catch (error) {
    console.error("Mediabunny 封装失败:", error);
    console.error("错误堆栈:", error.stack);
    throw error;
  }
};

const safeClose = () => {
  self.close();
};

let cachedBuffers = []; // 在没有解析 moov 之前缓存 buffer

self.onmessage = async (e) => {
  const { buffer, type, fileStart, chunks } = e.data;
  switch (type) {
    case "chunk":
      /**
       * 接收分片数据
       */
      {
        if (!mp4boxFile) {
          mp4boxFile = MP4Box.createFile();
          /**
           * @description onReady 只能是同步的调用
           * @param {*} info
           */
          mp4boxFile.onReady = (info) => {
            videoTrack = info.videoTracks[0];
            audioTrack = info.audioTracks[0];
            if (videoTrack) {
              const res = getDescription(mp4boxFile, audioTrack?.codec || "");
              videoDescription = res.videoDescription;
              audioDescription = res.audioDescription;
              /**
               * 发送 videoInfo 到 webcodec worker
               */
              self.postMessage({
                type: "videoInfo",
                data: {
                  videoTrack: {
                    codec: videoTrack.codec,
                    track_width: videoTrack.track_width,
                    track_height: videoTrack.track_height,
                    nb_samples: videoTrack.nb_samples,
                    duration: videoTrack.duration,
                    timescale: videoTrack.timescale,
                    framerate:
                      videoTrack.nb_samples /
                      (videoTrack.duration / videoTrack.timescale),
                    bitrate: videoTrack.bitrate,
                  },
                  videoDescription,
                },
              });
              // 设置样本提取选项
              if (audioTrack) {
                mp4boxFile.setExtractionOptions(audioTrack.id, "audio", {
                  nbSamples: audioTrack.nb_samples,
                });
              }
              mp4boxFile.setExtractionOptions(videoTrack.id, "video", {
                nbSamples: videoTrack.nb_samples,
              });
              mp4boxFile.start();
            } else {
              console.log("no video track?");
            }
          };
          mp4boxFile.onError = (error) => {
            console.log("mp4boxfile error:", error);
          };
          mp4boxFile.onSamples = (trackId, _user, samples) => {
            if (trackId === videoTrack.id) {
              videoDone = true;
            } else if (trackId === audioTrack?.id) {
              audioDone = true;
            }

            if (mediaDone) return;

            console.log(`轨道 ${trackId} 收到 ${samples.length} 个样本`);

            if (trackId === audioTrack?.id) {
              audioSamples = samples;
            } else {
              const videoSamples = [];
              const videoSampleData = [];
              for (const sample of samples) {
                videoSamples.push({
                  type: sample.is_sync ? "key" : "delta",
                  timestamp: (sample.cts * 1000000) / videoTrack.timescale, // 转换为微秒
                  duration: (sample.duration * 1000000) / videoTrack.timescale,
                  data: sample.data.buffer,
                });
                videoSampleData.push(sample.data.buffer);
              }
              /**
               * 发送 samples 到 webcodec worker
               */
              self.postMessage(
                {
                  type: "samples",
                  samples: videoSamples,
                },
                videoSampleData,
              );
            }

            if (videoDone && audioDone) {
              mediaDone = true;
            }
          };
          mp4boxFile.onError = (error) => {
            console.log(error);
          };
        }
        buffer.fileStart = fileStart;
        mp4boxFile.appendBuffer(buffer);
        if (!mp4boxFile.moov) {
          cachedBuffers.push(buffer);
        } else {
          if (cachedBuffers.length) {
            for (const cachedBuffer of cachedBuffers) {
              mp4boxFile.appendBuffer(cachedBuffer);
            }
            cachedBuffers = [];
          }
        }
        self.postMessage({
          type: "nextChunk",
          sendLast: !mp4boxFile.moov && fileStart === 0,
        });
      }
      break;
    case "eof":
      /**
       * 分片结束标识
       */
      {
        mp4boxFile && mp4boxFile.flush();
      }
      break;
    case "muxVideo":
      /**
       * 合成视频
       */
      {
        muxVideo(chunks).then(
          (buffer) => {
            self.postMessage({
              type: "success",
              buffer,
            });
          },
          (error) => {
            self.postMessage({
              type: "error",
              message: error.error,
            });
          },
        );
      }
      break;
    case "close":
      /**
       * 关闭 worker
       */
      safeClose();
      break;
    default:
      break;
  }
};

self.onerror = (err) => {
  self.postMessage({
    type: "error",
    message: err.message,
  });
};

/**
 * 通知主线程 ready
 */
self.postMessage({
  type: "init",
});
